import * as React from 'react'

import { Box, Button, Icon } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { InjectHoverProvider, useHover } from 'modules/core/lib/hoverContext'
import i18n from 'modules/core/lib/i18n'
import type { ModalStates } from 'modules/core/lib/questionModalStates'

import CustomFieldsField from 'modules/custom_resources/store/models/customFields/Field'
import DuplicateTemplateQuestionModal from 'modules/surveys/components/Form/QuestionConfigs/Duplicate'
import TemplateQuestionModal from 'modules/surveys/components/Form/QuestionConfigs/Edit'
import { useQuestionsTemplate } from 'modules/surveys/pages/QuestionsTemplates/MainContent/QuestionsContent/questionsTemplateContext'
import type TemplateQuestion from 'modules/surveys/store/models/TemplateQuestion'

import type {
  CreateTemplateQuestion,
  DeleteTemplateQuestion,
  UpdateTemplateQuestion,
} from 'modules/surveys/pages/QuestionsTemplates/MainContent/QuestionsContent/List/types'
import Actions from './Actions'
import CustomField from './CustomField'
import QuestionPosition from './QuestionPosition'

function DragAndDrop() {
  const isHover = useHover()

  if (!isHover) return null

  return <Icon.Medium icon="drag-and-drop" />
}

interface Props {
  templateQuestion: TemplateQuestion
  updateTemplateQuestion: (data: UpdateTemplateQuestion) => Promise<unknown>
  createTemplateQuestion: (
    data: CreateTemplateQuestion,
    questionsTemplateCategoryId?: number
  ) => Promise<unknown>
  deleteTemplateQuestion: (data: DeleteTemplateQuestion) => Promise<unknown>
  isCore: boolean
}

function Item({
  templateQuestion,
  updateTemplateQuestion,
  createTemplateQuestion,
  deleteTemplateQuestion,
  isCore,
}: Props) {
  const [openedModal, setOpenedModal] = React.useState<ModalStates>('none')
  const { isDefault } = useQuestionsTemplate()
  const field = new CustomFieldsField(templateQuestion.attributes.custom_field)

  const { running: deleting, performAction: onDelete } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      await deleteTemplateQuestion({
        id: templateQuestion.attributes.id,
      })
      closePortal()
    }
  )

  const onEditSubmit = async (data: Record<string, unknown>) => {
    await updateTemplateQuestion({
      template_question_id: templateQuestion.attributes.id,
      custom_field_params: data,
    })
  }

  const onDuplicateSubmit = async (data: Record<string, unknown>) => {
    await createTemplateQuestion(
      data,
      templateQuestion.attributes.surveys_questions_template_category_id
    )
  }

  return (
    <>
      <InjectHoverProvider>
        <Box flexDirection="row" paddingRight="s16" paddingLeft="s8" flexGrow width="full">
          {!isDefault && (
            <Box paddingTop="s24" paddingLeft="s8" width="s24" flexNoShrink>
              <DragAndDrop />
            </Box>
          )}
          <Box paddingTop="s24" paddingLeft="s16">
            <QuestionPosition templateQuestion={templateQuestion} />
          </Box>
          <Box padding="s24" gap="s8" flexGrow overflowY="hidden">
            <CustomField customField={templateQuestion.attributes.custom_field} />
          </Box>
          {!isCore && (
            <Box paddingTop="s24">
              <Actions templateQuestion={templateQuestion} setOpenedModal={setOpenedModal} />
            </Box>
          )}
        </Box>
      </InjectHoverProvider>

      {openedModal === 'edit' && (
        <TemplateQuestionModal
          field={field}
          onClose={() => setOpenedModal('none')}
          onSubmit={onEditSubmit}
        />
      )}

      {openedModal === 'duplicate' && (
        <DuplicateTemplateQuestionModal
          field={field}
          onClose={() => setOpenedModal('none')}
          onSubmit={onDuplicateSubmit}
        />
      )}

      {openedModal === 'delete' && (
        <Modal
          title={i18n.t('surveys.form.question_configs.confirm_delete.title')}
          description={i18n.t('surveys.form.question_configs.confirm_delete.description')}
          illustration="alert"
          onClose={() => setOpenedModal('none')}
          onSubmit={onDelete}
          footer={
            <Button type="negative" disabled={deleting} submit>
              {i18n.t('surveys.form.question_configs.confirm_delete.submit')}
            </Button>
          }
        />
      )}
    </>
  )
}

export default observer(Item)
